/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import {
    object, shape, string, number, bool, func,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { useQuery } from 'react-apollo';
import mbpLogger from 'mbp-logger';
import { compose } from 'recompose';
import Media from 'react-media';
import { bindActionCreators } from 'redux';
// components
import PLASkeleton from '../GraphqlSkeletonComponents/PLASkeleton';
import PDPSkeleton from '../GraphqlSkeletonComponents/PDPSkeleton';
import MobilePDPSkeleton from '../GraphqlSkeletonComponents/MobileSkeleton/MobilePDPSkeleton';
import GraphqlProductPageBuilder from './GraphqlProductPageBuilder';
// redux, hooks, helpers
import { GRAPHQL_ENV } from '../../../gql';
import findPassportByPartNumber from '../../../gql/queries/findPassportByPartNumber';
import * as app from '../../../../state/ducks/App';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import { getPassportData } from '../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { setPassportBundleData } from '../../../../state/ducks/Checkout/ducks/Payment/ducks/Passport/Passport-Action';

const brandSelectors = app.ducks.brand.selectors;

const GraphqlProductPageContainerWithPassport = ({
    location, brand, page, presentation_family, passportData: { item_id, price }, productBaseContent, isPlaEnabled, seoData, urlParams, isPassportBundleItem, baseProductPartNumber, content, product, isProductListingAd, setBundleData,
}) => {
    const passportBundle = productBaseContent?.entries?.[0]?.passport_bundle;
    const passportPartNumber = passportBundle?.passportsku?.[0]?.item_sku || item_id;
    const PRODUCT_BY_PART = findPassportByPartNumber(brand?.domain, passportPartNumber);
    const { loading, error, data } = useQuery(PRODUCT_BY_PART);

    if (loading) {
        if (isPlaEnabled && isProductListingAd(location.search)) {
            return <PLASkeleton />;
        }
        return (
            <Media
                query="(min-width: 599px)"
            >
                {(matches) => (matches
                    ?  <PDPSkeleton />
                    :  <MobilePDPSkeleton />

                )}
            </Media>
        );
    }

    if (error) {
        mbpLogger.logError({
            query: PRODUCT_BY_PART,
            component: 'GraphqlProductPageContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    if (!data) {
        mbpLogger.logWarning({
            query: PRODUCT_BY_PART,
            component: 'GraphqlProductPageContainer.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        return null;
    }

    const {
        product: passportProduct,
    } = data.findProductPageByPartNumber;
    const isPassportBundleProduct = {};
    isPassportBundleProduct.enable = passportBundle?.check_to_enable_passport_bundle;
    isPassportBundleProduct.details = passportBundle?.passportsku?.[0] || {};
    isPassportBundleProduct.skuText = passportBundle?.sku_label_text;

    // check for, if custom passport sku is passed or not
    if (passportProduct === null) {
        isPassportBundleProduct.prices = {
            value: price,
        };
        isPassportBundleProduct.passportSku = item_id;
    }

    if (passportProduct !== null) {
        isPassportBundleProduct.prices = passportProduct?.prices?.[0];
        isPassportBundleProduct.passportSku = passportProduct?.baseCode;
    }

    if (isPassportBundleProduct?.enable) {
        const bundleData = {
            enabled: true,
            passportSku: passportPartNumber,
            productSku: productBaseContent?.entries?.[0]?.product_base_code,
        };
        setBundleData(bundleData);
    }
    return (
        <div id="product_page_container">
            <GraphqlProductPageBuilder
                content={content}
                productBaseContent={productBaseContent}
                seoData={seoData}
                product={product}
                urlParams={urlParams}
                page={page}
                isPlaEnabled={isPlaEnabled}
                isPla={isProductListingAd(location.search)}
                presentation_family={presentation_family}
                isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                isPassportBundleProduct={isPassportBundleProduct || {}}
            />
        </div>
    );
};

GraphqlProductPageContainerWithPassport.propTypes = {
    brand: object.isRequired,
    page: shape({
        code: string,
    }).isRequired,
    location: object.isRequired,
    presentation_family: string.isRequired,
    passportData: shape({
        item_id: number,
    }),
    productBaseContent: object.isRequired,
    isPlaEnabled: bool.isRequired,
    seoData: object.isRequired,
    urlParams: object.isRequired,
    isPassportBundleItem: bool.isRequired,
    baseProductPartNumber: object.isRequired,
    content: object.isRequired,
    product: object.isRequired,
    isProductListingAd: object.isRequired,
    setBundleData: func.isRequired,
};

GraphqlProductPageContainerWithPassport.defaultProps = {
    passportData: {},
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
    featureFlags: getFeatureFlags(state),
    presentation_family: getPresentationFamily(state),
    passportData: getPassportData(state),
});
const mapDispatchToProps = (dispatch) => ({
    setBundleData: bindActionCreators(setPassportBundleData, dispatch),
});

const enhance = compose(
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
    }),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlProductPageContainerWithPassport);
